import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const PrintifyUrlEditor = () => {
  const { token, user, isSuperAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [customUrl, setCustomUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isSuperAdmin) {
      fetchProducts();
    }
  }, [user, isSuperAdmin]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/api/printify/sidebar-products`);
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (product) => {
    setEditingProduct(product);
    setCustomUrl(product.custom_store_url || '');
  };

  const saveUrl = async () => {
    if (!editingProduct || !customUrl.trim()) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/api/printify/products/${editingProduct.id}/custom-url`, {
        custom_store_url: customUrl.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setProducts(products.map(p => 
        p.id === editingProduct.id 
          ? { ...p, custom_store_url: customUrl.trim() }
          : p
      ));
      
      setEditingProduct(null);
      setCustomUrl('');
      alert('Store URL updated successfully!');
    } catch (error) {
      console.error('Error updating URL:', error);
      alert('Error updating URL: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setCustomUrl('');
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">Admin access required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">🛍️ Printify Store URL Editor</h1>
      
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Printify Products ({products.length})</h2>
          <p className="text-gray-600 text-sm mt-1">Edit store URLs to point to correct product pages</p>
        </div>

        <div className="divide-y divide-gray-200">
          {products.map((product) => (
            <div key={product.id} className="p-6">
              <div className="flex items-start gap-4">
                {/* Product Image */}
                {product.images && product.images[0] && (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                )}
                
                {/* Product Info */}
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {product.title}
                  </h3>
                  
                  {editingProduct?.id === product.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Store URL
                        </label>
                        <input
                          type="url"
                          value={customUrl}
                          onChange={(e) => setCustomUrl(e.target.value)}
                          placeholder="https://modernhumanbeing.printify.me/product/..."
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={saveUrl}
                          disabled={saving || !customUrl.trim()}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save URL'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        <strong>Current URL:</strong>{' '}
                        {product.custom_store_url ? (
                          <a href={product.custom_store_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {product.custom_store_url}
                          </a>
                        ) : (
                          <span className="text-red-500">No custom URL set</span>
                        )}
                      </div>
                      
                      <button
                        onClick={() => startEdit(product)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Edit Store URL
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrintifyUrlEditor;